import { components } from "@/api-schema";
import { MergeSelection } from "@/api-schema-enums";
import { cn } from "@/common/helpers/utils";
import { Check } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type MergeFieldPreview = components["schemas"]["MergeFieldPreviewResponse"];
export type MergeEntityRef = components["schemas"]["MergeEntitySummaryResponse"];

interface MergeFieldRowProps {
  field: MergeFieldPreview;
  choice: MergeSelection;
  onChange: (choice: MergeSelection) => void;
  sourceValueNode?: ReactNode;
  targetValueNode?: ReactNode;
}

export const MergeFieldRow = ({ field, choice, onChange, sourceValueNode, targetValueNode }: MergeFieldRowProps) => {
  const { t } = useTranslation("common");

  const display = (value: string | null | undefined) =>
    value == null || value === "" ? <span className="text-muted-foreground italic">{t("Empty")}</span> : value;

  return (
    <div className="border-border grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 border-b py-2 last:border-b-0">
      <button
        type="button"
        disabled={field.identical}
        onClick={() => onChange(MergeSelection.Source)}
        className={cn(
          "flex min-w-0 items-center gap-2 rounded-md border p-2 text-left text-sm transition-all",
          "disabled:pointer-events-none disabled:opacity-50",
          choice === MergeSelection.Source
            ? "border-primary/30 bg-primary/10 dark:border-primary/30 dark:bg-primary/10"
            : "hover:bg-primary/20 hover:border-primary/60 hover:dark:bg-primary/20 hover:dark:border-primary/60 cursor-pointer",
        )}
      >
        {choice === MergeSelection.Source && <Check className="text-primary h-3.5 w-3.5 shrink-0" />}
        {sourceValueNode ?? <span className="min-w-0 break-words">{display(field.sourceValue)}</span>}
      </button>

      <span className="text-muted-foreground w-20 shrink-0 self-center text-center text-xs font-medium">
        {t(`masterdata:MergeField-${field.key}`, { defaultValue: field.key })}
      </span>

      <button
        type="button"
        disabled={field.identical}
        onClick={() => onChange(MergeSelection.Target)}
        className={cn(
          "flex min-w-0 items-center gap-2 rounded-md border p-2 text-left text-sm transition-all",
          "disabled:pointer-events-none disabled:opacity-50",
          choice === MergeSelection.Target
            ? "border-primary/30 bg-primary/10 dark:border-primary/30 dark:bg-primary/10"
            : "hover:bg-primary/20 hover:border-primary/60 hover:dark:bg-primary/20 hover:dark:border-primary/60 cursor-pointer",
        )}
      >
        {choice === MergeSelection.Target && <Check className="text-primary h-3.5 w-3.5 shrink-0" />}
        {targetValueNode ?? <span className="min-w-0 break-words">{display(field.targetValue)}</span>}
      </button>
    </div>
  );
};
